import AddAPhotoIcon from "@mui/icons-material/AddAPhoto";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import IconButton from "@mui/material/IconButton";
import { useEffect, useState } from "react";
import LoadingBackdrop from "../../../components/LoadingBackdrop.js";
import { useUser } from "../../../components/UserContext.js";
import ErrorDialog from "../../../components/dialogs/ErrorDialog.js";
import FeedbackDialog from "../../../components/dialogs/FeedbackDialog.js";
import { postUpload, putUser } from "../../../lib/apiConfig.js";
import { getFileExtension } from "../../../lib/getFileExtension.js";
import { useFetch } from "../../../lib/hooks/useFetch.js";
import UploaderDialog from "./dialogs/UploaderDialog.js";

const getFileBuffer = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      resolve(event.target.result);
    };
    reader.onerror = (error) => {
      reject(error);
    };
    reader.readAsArrayBuffer(file);
  });

export default function UploadPhoto({ locales }) {
  const { user, runId, setUser } = useUser();
  const [file, setFile] = useState(null);
  const [fileBuffer, setFileBuffer] = useState(null);
  const [uploadUrl, setUploadUrl] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [showUploader, setShowUploader] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const setError = (error) => {
    setErrorMessage(error?.message);
    setShowError(true);
  };

  const { fetchData: getSignedUrl, isFetching: isGettingSignedUrl } = useFetch({
    endpoint: postUpload(),
    onSuccess: ({ fileS3Url, signedUrl }) => {
      setUploadUrl(signedUrl);
      setImageUrl(fileS3Url);
      setShowUploader(false);
    },
    onFail: setError,
  });

  const { fetchData: uploadPhoto, isFetching: isUploadingPhoto } = useFetch({
    endpoint: {
      url: uploadUrl,
      method: "PUT",
      headers: { "content-type": file?.type },
    },
    onSuccess: () => {
      setUploadUrl(null);
      setFileBuffer(null);
      updateUser({ imageUrl, run: { id: runId } });
    },
    onFail: (error) => {
      setError(error);
      setUploadUrl(null);
      setFileBuffer(null);
    },
  });

  const { fetchData: updateUser, isFetching: isUpdatingUser } = useFetch({
    endpoint: putUser({ id: user.id, runId: runId }),
    onSuccess: () => {
      setUser({ ...user, imageUrl });
      setShowUploader(false);
      setShowSuccess(true);
    },
    onFail: setError,
  });

  useEffect(() => {
    if (uploadUrl && fileBuffer) {
      uploadPhoto(fileBuffer);
    }
  }, [uploadUrl, fileBuffer, uploadPhoto]);

  const handleFileChange = async ({ target }) => {
    const fileBuffer = await getFileBuffer(target.files[0]);

    setFile(target.files[0]);
    setFileBuffer(fileBuffer);
  };

  const handleUpload = () => {
    file &&
      getSignedUrl({
        runId: runId,
        fileName: `${user.username}-${Date.now()}.${getFileExtension(
          file.name
        )}`,
        fileType: "image",
      });
  };

  return isGettingSignedUrl || isUploadingPhoto || isUpdatingUser ? (
    <LoadingBackdrop />
  ) : (
    <>
      <IconButton
        aria-label={locales["home.photo.button"]}
        color="secondary"
        onClick={() => setShowUploader(true)}
        size="large"
      >
        <AddAPhotoIcon />
      </IconButton>
      <UploaderDialog
        handleFileChange={handleFileChange}
        handleUpload={handleUpload}
        locales={locales}
        onClose={() => setShowUploader(false)}
        show={showUploader}
      />
      <FeedbackDialog
        bodyPrimary={locales["upload.success.content"]}
        icon={CheckCircleIcon}
        locales={locales}
        onClose={() => setShowSuccess(false)}
        show={showSuccess}
      />
      <ErrorDialog
        locales={locales}
        message={errorMessage}
        onClose={() => {
          setShowError(false);
          setErrorMessage(null);
        }}
        show={showError}
      />
    </>
  );
}
